import React from "react";

import { render, screen, waitFor, fireEvent } from "@testing-library/react";

import { GestorPainelView } from "./GestorPainel";
import { api } from "../../lib/api";

/* O degrau das REDES no painel do gestor.

   Contexto (AJUSTES_INTELIGENCIA_EDUCACIONAL.md, problema 6): o Administrador
   Educandus com "Todos" no modal via o ranking de REGIÕES de todas as redes
   misturadas — e os nomes se repetem entre clientes ("PE" existe em 5 grupos),
   então havia cinco linhas indistinguíveis. O nível de redes entrou acima, e o
   clique numa rede tem de levar ao fluxo padrão.

   O que estes casos protegem é a NAVEGAÇÃO, que nenhum teste de API alcança: a
   rota /grupos/ranking pode responder 200 e a tela ainda não deixar clicar. */

/* O Card revela-se ao entrar na viewport, e o jsdom 16 não traz
   IntersectionObserver nem scrollTo. Sem os dois stubs o componente nem monta —
   e o que se quer testar aqui é a navegação, não a animação. */
beforeAll(() => {
  global.IntersectionObserver = class {
    observe() {}

    unobserve() {}

    disconnect() {}
  };
  window.scrollTo = () => {};
});

jest.mock("../../lib/api", () => ({
  api: {
    gruposRanking: jest.fn(),
    regionalRanking: jest.fn(),
    kpisGlobal: jest.fn(),
    execucoesMensal: jest.fn(),
    faixas: jest.fn(),
    evolucao: jest.fn(),
    disciplinasDesempenho: jest.fn(),
    escolas: jest.fn()
  }
}));

// Os gráficos não são o objeto do teste, e recharts mede o DOM (que o jsdom não
// dimensiona) — trocados por marcadores.
jest.mock("../../components/charts/Charts", () => ({
  AreaChartCard: () => <div data-testid="area-chart" />,
  NotaBarraLista: () => <div data-testid="nota-barra" />
}));
jest.mock("../../components/charts/EvolucaoTemporal", () => ({
  EvolucaoTemporal: () => <div data-testid="evolucao" />
}));
jest.mock("../../components/charts/FaixaKpiCompacta", () => ({
  FaixaKpiCompacta: () => <div data-testid="faixa" />
}));

const KPIS = {
  regioes: 74,
  escolas: 1888,
  alunos: 207853,
  professores: 4171,
  turmas: 17030,
  tarefas_atribuidas: 2002,
  execucoes: 185,
  nota_media: 7.1,
  engajamento_medio: 19,
  tempo_total_seg: 4681184,
  inativos: 8117,
  nunca_acessaram: 244906,
  taxa_conclusao: 9.2,
  inativos_dias: 7
};

// Duas redes, com os campos que o backend devolve em /grupos/ranking.
const REDES = [
  {
    id: 80,
    rede: "FORMANDO CIDADÃOS",
    escolas: 990,
    turmas: 9695,
    alunos: 141586,
    professores: 1011,
    nota_media: 7.2,
    execucoes: 0,
    tarefas_atribuidas: 86,
    inativos: 1677,
    nunca_acessaram: 139789,
    tempo_total_seg: 1431351,
    taxa_conclusao: 0,
    engajamento_medio: 9.4,
    nota_tipo: "rede"
  },
  {
    id: 92,
    rede: "Sucesso Digital",
    escolas: 424,
    turmas: 3817,
    alunos: 59844,
    professores: 500,
    nota_media: 7,
    execucoes: 10,
    tarefas_atribuidas: 100,
    inativos: 900,
    nunca_acessaram: 40000,
    tempo_total_seg: 100000,
    taxa_conclusao: 10,
    engajamento_medio: 6.7,
    nota_tipo: "rede"
  }
];

const REGIOES = [
  {
    id: 171,
    regional: "PE",
    escolas: 167,
    turmas: 1669,
    alunos: 26725,
    professores: 132,
    nota_media: 7.3,
    execucoes: 5,
    tarefas_atribuidas: 50,
    inativos: 100,
    nunca_acessaram: 1000,
    tempo_total_seg: 5000,
    taxa_conclusao: 10,
    engajamento_medio: 9,
    nota_tipo: "rede"
  }
];

beforeEach(() => {
  jest.clearAllMocks();
  api.gruposRanking.mockResolvedValue(REDES);
  api.regionalRanking.mockResolvedValue(REGIOES);
  api.kpisGlobal.mockResolvedValue(KPIS);
  api.execucoesMensal.mockResolvedValue([]);
  api.faixas.mockResolvedValue([]);
  api.evolucao.mockResolvedValue([]);
  api.disciplinasDesempenho.mockResolvedValue([]);
  api.escolas.mockResolvedValue({ total: 0, rows: [] });
});

const periodo = { inicio: null, fim: null };

function renderizarNivelEducandus(props = {}) {
  return render(
    <GestorPainelView
      educandus
      grupoId={null}
      grupoNome="Todas as redes"
      periodo={periodo}
      {...props}
    />
  );
}

describe("nível da Educandus (sem rede escolhida)", () => {
  it("lista as REDES, não as regiões", async () => {
    renderizarNivelEducandus();

    expect(await screen.findByText("Ranking de redes")).toBeInTheDocument();
    expect(await screen.findByText("FORMANDO CIDADÃOS")).toBeInTheDocument();
    expect(screen.getByText("Sucesso Digital")).toBeInTheDocument();

    // A rota de regiões nem é chamada: sem rede, ela traria as regiões de todos
    // os clientes juntas — o defeito que este nível resolve.
    expect(api.gruposRanking).toHaveBeenCalled();
    expect(api.regionalRanking).not.toHaveBeenCalled();
  });

  /**
   * O caso que o Kilson relatou: "a tela do ranking não me deixa clicar na rede
   * que quero explorar".
   */
  it("deixa CLICAR numa rede e desce para as regiões dela", async () => {
    renderizarNivelEducandus();

    const linha = await screen.findByText("FORMANDO CIDADÃOS");

    fireEvent.click(linha);

    // O bloco troca de nível...
    expect(await screen.findByText("Ranking de regiões")).toBeInTheDocument();

    // ...e as chamadas passam a levar o grupo escolhido.
    await waitFor(() => expect(api.regionalRanking).toHaveBeenCalledWith(80));
    expect(await screen.findByText("PE")).toBeInTheDocument();
  });

  it("o breadcrumb ganha a rede escolhida, e permite voltar", async () => {
    const onBreadcrumb = jest.fn();
    renderizarNivelEducandus({ onBreadcrumb });

    fireEvent.click(await screen.findByText("Sucesso Digital"));

    await waitFor(() => {
      const ultimo =
        onBreadcrumb.mock.calls[onBreadcrumb.mock.calls.length - 1][0];
      expect(ultimo.map(c => c.label)).toEqual([
        "Todas as redes",
        "Sucesso Digital"
      ]);
      // O primeiro nível tem de ser clicável — é por ele que se volta.
      expect(typeof ultimo[0].onClick).toBe("function");
    });
  });
});

/* ---------------------------------------------------------------------------
   O defeito que apareceu no primeiro teste em tela (relato do Kilson): "a tela
   do ranking não me deixa clicar na rede que quero explorar".

   Causa: o degrau das redes só existia quando `grupoId` era nulo. Quem tivesse
   escolhido uma rede no modal do AVA — e o redux-persist guarda a escolha entre
   sessões — abria o painel PRESO nela: o ranking mostrava as regiões daquela
   rede, e nenhuma rede para clicar. A escolha do modal é ponto de partida, não
   recorte permanente.
   ------------------------------------------------------------------------- */
describe("Educandus com uma rede já escolhida no modal do AVA", () => {
  it("abre dentro da rede, mas deixa VOLTAR ao ranking de redes", async () => {
    render(
      <GestorPainelView
        educandus
        grupoId={80}
        grupoNome="FORMANDO CIDADÃOS"
        periodo={periodo}
      />
    );

    // Abre já na rede escolhida — o modal continua valendo como atalho.
    expect(await screen.findByText("Ranking de regiões")).toBeInTheDocument();
    await waitFor(() => expect(api.regionalRanking).toHaveBeenCalledWith(80));

    // E o breadcrumb tem o degrau de cima, clicável.
    const onBreadcrumb = jest.fn();
    render(
      <GestorPainelView
        educandus
        grupoId={80}
        grupoNome="FORMANDO CIDADÃOS"
        periodo={periodo}
        onBreadcrumb={onBreadcrumb}
      />
    );

    await waitFor(() => {
      const ultimo =
        onBreadcrumb.mock.calls[onBreadcrumb.mock.calls.length - 1][0];
      expect(ultimo.map(c => c.nivel)).toEqual(["educandus", "rede"]);
      expect(typeof ultimo[0].onClick).toBe("function");
    });
  });

  it("voltar ao topo traz o ranking de redes de novo", async () => {
    const onBreadcrumb = jest.fn();
    render(
      <GestorPainelView
        educandus
        grupoId={80}
        grupoNome="FORMANDO CIDADÃOS"
        periodo={periodo}
        onBreadcrumb={onBreadcrumb}
      />
    );

    await waitFor(() => expect(onBreadcrumb).toHaveBeenCalled());

    // Clica no primeiro nível do breadcrumb, como a interface faz.
    const trilha =
      onBreadcrumb.mock.calls[onBreadcrumb.mock.calls.length - 1][0];
    fireEvent.click(await screen.findByText("Ranking de regiões")); // no-op, só garante render
    trilha[0].onClick();

    expect(await screen.findByText("Ranking de redes")).toBeInTheDocument();
    expect(await screen.findByText("FORMANDO CIDADÃOS")).toBeInTheDocument();
    expect(api.gruposRanking).toHaveBeenCalled();
  });
});

/* ---------------------------------------------------------------------------
   Busca por nome nas listas (pedido do Kilson, 04/08/2026).

   O componente é um só (components/BuscaNome), mas os MODOS são dois, e é a
   diferença que estes casos protegem: redes e regiões filtram no cliente, porque
   vêm completas; escolas filtram NO SERVIDOR, porque a lista é paginada e
   filtrar no cliente esconderia da busca tudo o que está fora da página.
   ------------------------------------------------------------------------- */
describe("busca por nome", () => {
  it("filtra as redes no cliente, sem ir ao servidor de novo", async () => {
    renderizarNivelEducandus();

    await screen.findByText("FORMANDO CIDADÃOS");
    const chamadasAntes = api.gruposRanking.mock.calls.length;

    fireEvent.change(screen.getByPlaceholderText("Buscar rede…"), {
      target: { value: "sucesso" }
    });

    // Sobra só a rede que casa...
    expect(screen.queryByText("FORMANDO CIDADÃOS")).not.toBeInTheDocument();
    expect(screen.getByText("Sucesso Digital")).toBeInTheDocument();

    // ...e nenhuma requisição nova: a lista já estava toda em mãos.
    expect(api.gruposRanking.mock.calls.length).toBe(chamadasAntes);
  });

  it("acha rede sem o usuário digitar acento", async () => {
    renderizarNivelEducandus();
    await screen.findByText("FORMANDO CIDADÃOS");

    fireEvent.change(screen.getByPlaceholderText("Buscar rede…"), {
      target: { value: "cidadaos" }
    });

    expect(screen.getByText("FORMANDO CIDADÃOS")).toBeInTheDocument();
    expect(screen.queryByText("Sucesso Digital")).not.toBeInTheDocument();
  });

  it("diz quando a busca não acha nada, em vez de mostrar a lista toda", async () => {
    renderizarNivelEducandus();
    await screen.findByText("FORMANDO CIDADÃOS");

    fireEvent.change(screen.getByPlaceholderText("Buscar rede…"), {
      target: { value: "zzz" }
    });

    expect(screen.getByText(/Nenhuma rede corresponde a/)).toBeInTheDocument();
    expect(screen.queryByText("Sucesso Digital")).not.toBeInTheDocument();
  });

  /* Sem fake timers de propósito: eles congelam o relógio que o `waitFor` da
     testing-library usa para esperar o DOM, e o teste travava antes de chegar ao
     campo. O que importa provar é o efeito observável — teclas intermediárias
     NÃO viram requisição, e a última vira. */
  it("a busca de ESCOLAS vai ao servidor, e as teclas do meio não", async () => {
    render(
      <GestorPainelView
        grupoId={80}
        grupoNome="FORMANDO CIDADÃOS"
        periodo={periodo}
      />
    );

    // Entra numa região para a lista de escolas aparecer.
    fireEvent.click(await screen.findByText("PE"));

    const campo = await screen.findByPlaceholderText("Buscar escola…");

    fireEvent.change(campo, { target: { value: "m" } });
    fireEvent.change(campo, { target: { value: "mu" } });
    fireEvent.change(campo, { target: { value: "mun" } });

    // A busca completa chega ao servidor, dentro do escopo já recortado.
    await waitFor(() =>
      expect(api.escolas).toHaveBeenCalledWith(
        expect.objectContaining({ nome: "mun", grupo_id: 80 })
      )
    );

    // E os prefixos não geraram chamada: sem o debounce seriam três requisições,
    // duas delas descartadas — e a última a responder não é necessariamente a da
    // tecla mais recente.
    const termos = api.escolas.mock.calls.map(([p]) => p.nome);
    expect(termos).not.toContain("m");
    expect(termos).not.toContain("mu");
  });
});

describe("perfil preso a uma rede (gestor de rede, não Educandus)", () => {
  it("não mostra o degrau das redes, e vai direto às regiões", async () => {
    render(
      <GestorPainelView
        grupoId={80}
        grupoNome="FORMANDO CIDADÃOS"
        periodo={periodo}
      />
    );

    expect(await screen.findByText("Ranking de regiões")).toBeInTheDocument();
    expect(api.gruposRanking).not.toHaveBeenCalled();
    await waitFor(() => expect(api.regionalRanking).toHaveBeenCalledWith(80));
  });
});
